import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Button,
  HStack,
  Icon,
  IconButton,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useRef } from 'react';
import {
  MdRefresh,
  MdVisibility,
  MdAccountBalanceWallet,
  MdCheckCircle,
  MdCancel,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { PageShell, PageHeader } from 'components/layout/PageShell';
import {
  UnifiedDataTableShell,
  UnifiedFilterBar,
  UnifiedSurfaceCard,
} from 'components/layout/UnifiedPrimitives';
import { useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';
import API from 'api/api';
import { useTranslation } from 'i18n/TranslationProvider';
import {
  CATEGORIES,
  mapTemplateTitleToTranslation,
} from 'domains/shared/utils/categoryUtils';

const STATUS_OPTIONS = [
  '',
  'open',
  'has_quotes',
  'accepted',
  'paid',
  'planned',
  'in_transit',
  'pickedUp',
  'delivered',
  'completed',
  'canceled',
  'expired',
];

const statusColorMap = {
  open: 'blue',
  has_quotes: 'purple',
  accepted: 'purple',
  paid: 'green',
  planned: 'cyan',
  in_transit: 'cyan',
  pickedUp: 'teal',
  delivered: 'green',
  completed: 'teal',
  canceled: 'red',
  expired: 'gray',
};

function formatDistanceKm(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? `${n} km` : '—';
}

function formatDate(dateStr, language) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(language || 'en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export default function AdminListings() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t, language } = useTranslation();
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const { tableSx } = useUnifiedTableStyles();
  const cancelRef = useRef(null);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [confirmPaymentTarget, setConfirmPaymentTarget] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [refreshingStripePaymentId, setRefreshingStripePaymentId] =
    useState(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;

      const res = await API.get('/listings', { params });
      const data = res?.data?.data ?? res?.data ?? [];
      setListings(Array.isArray(data) ? data : (data.listings ?? []));
    } catch {
      toast({
        title: t('Failed to load listings'),
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, toast, t]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    const id = cancelTarget._id || cancelTarget.id;
    setCanceling(true);
    try {
      await API.delete(`/listings/${id}`);
      toast({
        title: t('Listing canceled'),
        status: 'success',
        duration: 2500,
      });
      setCancelTarget(null);
      fetchListings();
    } catch {
      toast({
        title: t('Failed to cancel listing'),
        status: 'error',
        duration: 3000,
      });
    } finally {
      setCanceling(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!confirmPaymentTarget) return;
    const id = confirmPaymentTarget._id || confirmPaymentTarget.id;
    setConfirmingPayment(true);
    try {
      await API.post(`/listings/${id}/confirm-payment`);
      toast({
        title: t('Payment confirmed — listing status set to paid'),
        status: 'success',
        duration: 3000,
      });
      setConfirmPaymentTarget(null);
      fetchListings();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || t('Failed to confirm payment'),
        status: 'error',
        duration: 3000,
      });
    } finally {
      setConfirmingPayment(false);
    }
  };

  const handleRefreshStripePayment = async (listing) => {
    const id = listing?._id || listing?.id;
    if (!id) return;

    setRefreshingStripePaymentId(id);
    try {
      const res = await API.post(`/listings/${id}/refresh-stripe-payment`);
      const stripe = res?.data?.stripe || {};
      const stripeStatus = stripe.status || 'unknown';

      const STATUS_LABELS = {
        succeeded: {
          title: t('Payment confirmed — listing updated to Paid'),
          status: 'success',
        },
        processing: {
          title: t('Payment is processing — check again in a moment'),
          status: 'info',
        },
        requires_payment_method: {
          title: t('Client has not completed payment yet (no card entered)'),
          status: 'warning',
        },
        requires_confirmation: {
          title: t('Payment requires confirmation from client'),
          status: 'warning',
        },
        requires_action: {
          title: t('Payment requires additional client action (e.g. 3D Secure)'),
          status: 'warning',
        },
        canceled: {
          title: t('Payment intent was canceled — a new payment will be needed'),
          status: 'error',
        },
      };

      const toastData = STATUS_LABELS[stripeStatus] ?? {
        title: t('Stripe status: {{status}}', { values: { status: stripeStatus } }),
        status: 'info',
      };

      toast({ ...toastData, duration: 4000, isClosable: true });
      fetchListings();
    } catch (err) {
      toast({
        title:
          err?.response?.data?.message ||
          t('Failed to refresh Stripe payment status'),
        status: 'error',
        duration: 3500,
      });
    } finally {
      setRefreshingStripePaymentId(null);
    }
  };

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={''}
          description={t('All transport listing requests across the platform.')}
        />

        <UnifiedSurfaceCard p={5}>
          <UnifiedFilterBar mb={5}>
            <Select
              maxW="180px"
              size="sm"
              borderRadius="12px"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              placeholder={t('All statuses')}
            >
              {STATUS_OPTIONS.filter(Boolean).map((s) => (
                <option key={s} value={s}>
                  {t(s)}
                </option>
              ))}
            </Select>

            <Select
              maxW="180px"
              size="sm"
              borderRadius="12px"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              placeholder={t('All categories')}
            >
              {CATEGORIES.filter(Boolean).map((c) => (
                <option key={c} value={c}>
                  {t(c.label)}
                </option>
              ))}
            </Select>
            <IconButton
              icon={<Icon as={MdRefresh} />}
              aria-label={t('Refresh')}
              variant="ghost"
              size="sm"
              isLoading={loading}
              onClick={fetchListings}
              borderRadius="12px"
            />
          </UnifiedFilterBar>

          <UnifiedDataTableShell
            loading={loading}
            loadingLabel={t('Loading listings...')}
            isEmpty={listings.length === 0}
            emptyTitle={t('No listings found')}
            emptyDescription={t('Try widening the current listing filters.')}
          >
            <Table size="sm" variant="simple" sx={tableSx}>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>{t('Title')}</Th>
                  <Th>{t('Category')}</Th>
                  <Th>{t('Route')}</Th>
                  <Th>{t('Distance')}</Th>
                  <Th>{t('Status')}</Th>
                  <Th>{t('Created')}</Th>
                  <Th>{t('Client')}</Th>
                  <Th>{t('Actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {listings.map((listing, index) => {
                  const id = listing._id || listing.id;
                  const normalizedStatus = String(
                    listing.status || '',
                  ).toLowerCase();
                  const normalizedPaymentStatus = String(
                    listing.paymentStatus || 'unpaid',
                  ).toLowerCase();
                  const canCheckStripe =
                    normalizedPaymentStatus !== 'paid' &&
                    ['accepted', 'awaiting_payment'].includes(normalizedStatus);
                  const from =
                    listing.fromCity ||
                    listing.pickupLocation?.city ||
                    listing.route?.startCity ||
                    listing.from?.city ||
                    '—';
                  const to =
                    listing.toCity ||
                    listing.deliveryLocation?.city ||
                    listing.route?.endCity ||
                    listing.to?.city ||
                    '—';
                  const clientName =
                    listing.client?.name ||
                    listing.clientName ||
                    listing.createdBy?.name ||
                    '—';

                  return (
                    <Tr key={id}>
                      <Td color={mutedText} fontSize="xs">
                        {index + 1}
                      </Td>
                      <Td fontWeight="600" maxW="180px" isTruncated>
                        {mapTemplateTitleToTranslation(listing.title, t) ||
                          listing.name ||
                          t('Listing {{id}}', {
                            values: { id: String(id).slice(-6) },
                          })}
                      </Td>
                      <Td textTransform="capitalize">
                        {t(listing.category) || '—'}
                      </Td>
                      <Td>
                        {from} → {to}
                      </Td>
                      <Td color={mutedText} fontSize="xs">
                        {formatDistanceKm(listing.distanceKm)}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={statusColorMap[listing.status] || 'gray'}
                          borderRadius="full"
                          px={2}
                        >
                          {t(listing.status || 'unknown')}
                        </Badge>
                      </Td>
                      <Td color={mutedText} fontSize="xs">
                        {formatDate(listing.createdAt, language)}
                      </Td>
                      <Td>{clientName}</Td>
                      <Td>
                        <HStack spacing={2} flexWrap="wrap">
                          <Button
                            size="xs"
                            variant="action"
                            leftIcon={<Icon as={MdVisibility} />}
                            onClick={() => navigate(`/admin/listings/${id}`)}
                          >
                            {t('View')}
                          </Button>
                          {canCheckStripe ? (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="purple"
                              leftIcon={<Icon as={MdAccountBalanceWallet} />}
                              isLoading={refreshingStripePaymentId === id}
                              onClick={() =>
                                handleRefreshStripePayment(listing)
                              }
                            >
                              {t('Check Stripe')}
                            </Button>
                          ) : null}
                          {['accepted', 'awaiting_payment'].includes(
                            normalizedStatus,
                          ) && (
                            <Button
                              size="xs"
                              variant="solid"
                              colorScheme="green"
                              leftIcon={<Icon as={MdCheckCircle} />}
                              onClick={() => setConfirmPaymentTarget(listing)}
                            >
                              {t('Confirm Payment')}
                            </Button>
                          )}
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            leftIcon={<Icon as={MdCancel} />}
                            onClick={() => setCancelTarget(listing)}
                            isDisabled={listing.status === 'canceled'}
                          >
                            {t('Cancel')}
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </UnifiedDataTableShell>
        </UnifiedSurfaceCard>
      </VStack>

      <AlertDialog
        isOpen={Boolean(cancelTarget)}
        leastDestructiveRef={cancelRef}
        onClose={() => (canceling ? null : setCancelTarget(null))}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">
            {t('Cancel listing')}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>
              {t(
                'This will soft-cancel the listing for the client and remove it from active marketplace views.',
              )}
            </Text>
            <Text fontWeight="700">
              {cancelTarget?.title ||
                cancelTarget?.humanId ||
                t('Selected listing')}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={() => setCancelTarget(null)}
              isDisabled={canceling}
            >
              {t('Keep listing')}
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={handleCancel}
              isLoading={canceling}
              loadingText={t('Canceling...')}
            >
              {t('Cancel listing')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        isOpen={Boolean(confirmPaymentTarget)}
        leastDestructiveRef={cancelRef}
        onClose={() =>
          confirmingPayment ? null : setConfirmPaymentTarget(null)
        }
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent borderRadius="2xl">
          <AlertDialogHeader fontSize="lg" fontWeight="800">
            {t('Confirm payment received')}
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>
              {t(
                'Confirm that you have received the bank transfer or payment for this listing. This will move the listing to Paid status and allow the partner to mark it as planned.',
              )}
            </Text>
            <Text fontWeight="700" color="green.600">
              {confirmPaymentTarget?.title ||
                confirmPaymentTarget?.humanId ||
                t('Selected listing')}
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={() => setConfirmPaymentTarget(null)}
              isDisabled={confirmingPayment}
            >
              {t('Cancel')}
            </Button>
            <Button
              colorScheme="green"
              ml={3}
              onClick={handleConfirmPayment}
              isLoading={confirmingPayment}
              loadingText={t('Confirming...')}
            >
              {t('Confirm Payment')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
